import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { VERSION_FIELD_DESCRIPTOR } from './_versionField.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_ORDER, ProductionOrderStatusValues, PRODUCTION_ORDER_STATUS_LABELS, } from '../types/production-order.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for the production order header (org/{orgId}/production-order).
 *
 * No lifecycle fields (transactional — see types/production-order.ts); the only
 * lifecycle is the business `status`. Keeps the `Auditable` block. Mono-product
 * header; planned grade is the `ProductionOrderBreakdown` child.
 */
export const productionOrderDescriptor = {
    name: PRODUCTION_ORDER,
    label: 'Ordem de produção',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-order',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 7,
    description: 'Production order header (mono-product) with planned quantity/grade and a status state machine',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: entidade versionada (spec 0045) — getById em cache devolve versão velha e anula a detecção de conflito.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique production order identifier',
        },
        {
            name: 'code',
            example: 1001,
            label: 'Código',
            type: 'number',
            display: 'code',
            required: true,
            unique: true,
            autoNumber: true,
            description: 'Production order code (manual integer, unique per organization; positive, capped at MAX_CODE_SMALL)',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'Product to produce (ProductRef.productId)',
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
            description: 'Partial ProductVariant (identifying axes; ProductRef.variantId). Size lives on ProductionOrderBreakdown.',
        },
        {
            name: 'quantity',
            example: 250,
            label: 'Quantidade',
            type: 'number',
            display: 'quantity',
            required: true,
            description: 'Total planned quantity',
        },
        {
            name: 'enteredQuantity',
            example: 1700,
            label: 'Quantidade digitada',
            type: 'number',
            display: 'quantity',
            required: false,
            description: 'Quantity as typed in an alternate product unit (memory; base quantity remains authoritative)',
        },
        {
            name: 'enteredUnitId',
            label: 'Unidade digitada',
            type: 'string',
            required: false,
            description: 'FK ProductUnit used when typing enteredQuantity (spec 0050 D4)',
        },
        {
            name: 'technicalSheetId',
            label: 'Ficha técnica',
            type: 'string',
            required: false,
            description: 'Optional technical sheet (D7 — OP without a sheet is valid)',
        },
        {
            name: 'factoryId',
            label: 'Fábrica',
            type: 'string',
            required: false,
            description: 'Optional factory where this production order will be produced',
        },
        {
            name: 'salesOrderId',
            label: 'Pedido',
            type: 'string',
            required: false,
            description: 'Informative sales-order link (no automation)',
        },
        {
            name: 'salesOrderItemId',
            example: 'soi-5531-1',
            label: 'Item do pedido',
            type: 'string',
            required: false,
            description: 'Plain pointer to a SalesOrderItem (no FK — resolution requires sales order context)',
        },
        {
            name: 'lotNumber',
            example: 'L-2026-10-031',
            label: 'Lote',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
            description: 'Traceability lot number (free text)',
        },
        {
            name: 'outputStockLocationId',
            label: 'Local de entrada do PA',
            type: 'string',
            required: false,
            description: 'Stock location for finished-goods entry (F4). Null inherits production org param → stock.defaultStockLocationId.',
        },
        {
            name: 'materialsStockLocationId',
            label: 'Local de consumo de insumos',
            type: 'string',
            required: false,
            description: 'Stock location for material backflush (0180). Null inherits the same cascade as outputStockLocationId.',
        },
        {
            name: 'issueDate',
            example: '2026-10-21',
            label: 'Data de emissão',
            type: 'date',
            required: false,
        },
        {
            name: 'plannedStartDate',
            example: '2026-10-21',
            label: 'Início planejado',
            type: 'date',
            required: false,
        },
        {
            name: 'plannedEndDate',
            example: '2026-10-24',
            label: 'Fim planejado',
            type: 'date',
            required: false,
        },
        {
            name: 'deliveryDate',
            example: '2026-10-28',
            label: 'Data de entrega',
            type: 'date',
            required: false,
        },
        {
            name: 'status',
            example: 'in-progress',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...ProductionOrderStatusValues],
            enumLabels: PRODUCTION_ORDER_STATUS_LABELS,
            enumTones: {
                draft: 'neutral',
                released: 'info',
                'in-progress': 'warning',
                completed: 'success',
                cancelled: 'destructive',
            },
            description: 'Production order status (draft → released → in-progress → completed | cancelled)',
            defaultValue: 'draft',
        },
        {
            name: 'releasedAt',
            example: '2026-10-21',
            label: 'Liberada em',
            type: 'date',
            display: 'datetime',
            required: false,
            description: 'Release timestamp (snapshot provenance for 0169)',
        },
        {
            name: 'notes',
            example: 'Pedido 5531 — entregar no portão 2',
            label: 'Observações',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        ...AUDIT_FIELD_DESCRIPTORS,
        VERSION_FIELD_DESCRIPTOR,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto a produzir',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante parcial (identificadores; sem tamanho)',
        },
        {
            fieldName: 'enteredUnitId',
            targetEntity: 'ProductUnit',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Unidade do produto usada na digitação (memória)',
        },
        {
            fieldName: 'technicalSheetId',
            targetEntity: 'TechnicalSheet',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Ficha técnica (opcional)',
        },
        {
            fieldName: 'factoryId',
            targetEntity: 'Factory',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Fábrica onde a OP será produzida (opcional)',
        },
        {
            fieldName: 'salesOrderId',
            targetEntity: 'SalesOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Pedido de venda vinculado (informativo)',
        },
        {
            fieldName: 'outputStockLocationId',
            targetEntity: 'StockLocation',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Local de entrada do produto acabado',
        },
        {
            fieldName: 'materialsStockLocationId',
            targetEntity: 'StockLocation',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Local de consumo de insumos',
        },
        {
            fieldName: 'id',
            targetEntity: 'ProductionOrderBreakdown',
            relationship: 'one-to-many',
            description: 'Grade planejada (tamanhos)',
        },
        {
            fieldName: 'id',
            targetEntity: 'ProductionOrderStep',
            relationship: 'one-to-many',
            description: 'Etapas planejadas (snapshot no release)',
        },
        {
            fieldName: 'id',
            targetEntity: 'ProductionOrderMaterial',
            relationship: 'one-to-many',
            description: 'Insumos planejados (snapshot no release)',
        },
        {
            fieldName: 'id',
            targetEntity: 'ProductionTicket',
            relationship: 'one-to-many',
            description: 'Talões gerados a partir desta ordem (coleção de topo)',
        },
        {
            fieldName: 'id',
            targetEntity: 'ProductionRun',
            relationship: 'one-to-many',
            description: 'Execuções de produção desta ordem (coleção de topo)',
        },
    ],
};
//# sourceMappingURL=production-order.descriptor.js.map